<template>
  <div class="chart-box">
    <div class="chart-top">
      <div class="chart-top-lt">
        <h2>
          {{ constants.PROJECTS_TEXT }}
        </h2>
      </div>
      <div class="chart-top-rt">
        <div class="issues--radio">
          <ul>
            <li>
              <input
                type="radio"
                name="radiog_download"
                id="createdIssuesRadio"
                class="css-checkbox"
              />
              <label for="createdIssuesRadio" class="css-label">
                {{ constants.CREATED_ISSUES_BUTTON }}
              </label>
            </li>
            <li>
              <input
                type="radio"
                name="radiog_download"
                id="resolvedIssuesRadio"
                checked="true"
                class="css-checkbox"
              />
              <label for="resolvedIssuesRadio" class="css-label">
                {{ constants.RESOLVED_ISSUES_BUTTON }}
              </label>
            </li>
          </ul>
        </div>
        <!-- <div class="calendar-drop-down-box">
          <div class="calendar-drop-down-icon">
            <img src="@/assets/lems-v1/images/calendar.svg" alt="" />
          </div>
          <div class="calendar-drop-down-content">
            <div class="calendar-drop-down-content-inner">
              <div class="choose-calendar">
                <img
                  src="@/assets/lems-v1/images/drop-down-calender.jpg"
                  alt=""
                />
              </div>
              <div class="custom-filter">
                <button id="allCalendarBtn">All</button>
                <button id="lastWeekCalendarBtn">Last Week</button>
                <button id="lastMonthCalendarBtn">Last Month</button>
                <button id="lastYearCalendarBtn">Last Year</button>
              </div>
            </div>
          </div>
        </div> -->
      </div>
    </div>
    <!-- chart-btm area intentionally remains commented/unchanged; chart canvas and spinner are injected dynamically -->
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from "vue";
import constants from "@/utils/en-US-Employee.json";
import axios from "axios";
import { API_BASE_URL } from "@/config/api.config";
import { Chart } from "chart.js/auto";

interface MonthlyPoint {
  month: string;
  created: number;
  resolved: number;
}

interface ProjectMonthlyData {
  project_name: string;
  project_key: string;
  monthly_data: MonthlyPoint[];
}

export default defineComponent({
  name: "Chart3",
  data() {
    return {
      constants,
      username: "",
      selectedMetric: "created" as "created" | "resolved",
      chart: null as any,
      months: [] as string[],
      projectsData: {} as Record<string, ProjectMonthlyData>,
      isLoading: false,
      isRendering: false,
      renderTimer: null as any,
    };
  },
  async mounted() {
    // Get username from localStorage
    this.username = localStorage.getItem("jira_user_username") || "";

    // Default to Created Issues (first radio) without changing HTML
    const createdRadio = document.getElementById("createdIssuesRadio") as HTMLInputElement | null;
    const resolvedRadio = document.getElementById("resolvedIssuesRadio") as HTMLInputElement | null;
    if (createdRadio) createdRadio.checked = true;
    if (resolvedRadio) resolvedRadio.checked = false;

    // Wire radio listeners
    createdRadio?.addEventListener("change", () => {
      if (createdRadio.checked) {
        this.selectedMetric = "created";
        this.queueRender();
      }
    });
    resolvedRadio?.addEventListener("change", () => {
      if (resolvedRadio.checked) {
        this.selectedMetric = "resolved";
        this.queueRender();
      }
    });

    await this.fetchAndRender();
  },
  methods: {
    queueRender() {
      if (this.renderTimer) {
        clearTimeout(this.renderTimer);
        this.renderTimer = null;
      }
      this.renderTimer = setTimeout(() => {
        this.$nextTick(() => {
          requestAnimationFrame(() => this.renderChart());
        });
      }, 50);
    },
    async fetchAndRender() {
      this.isLoading = true;
      this.showSpinner();
      try {
        await this.fetchAllProjectsMonthlyIssues();
        await nextTick();
        this.injectCanvasAndRender();
      } catch (err) {
        console.error("Error preparing chart:", err);
      } finally {
        this.isLoading = false;
        this.hideSpinner();
      }
    },
    async fetchAllProjectsMonthlyIssues() {
      if (!this.username) return;
      try {
        const url = `${API_BASE_URL}/jira/all-projects-monthly-issues/${this.username}/`;
        const resp = await axios.get(url);
        const payload = resp.data?.projects_monthly_data || {};
        this.projectsData = payload as Record<string, ProjectMonthlyData>;
        this.buildMonthsAxis();
      } catch (error) {
        console.error("Failed to fetch all projects monthly issues:", error);
        this.projectsData = {};
        this.months = [];
      }
    },
    buildMonthsAxis() {
      const monthOrder = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      const monthSet = new Set<string>();
      Object.values(this.projectsData).forEach((proj) => {
        proj.monthly_data.forEach((pt) => monthSet.add(pt.month));
      });
      const monthsSorted = monthOrder.filter((m) => monthSet.size === 0 || monthSet.has(m));
      this.months = monthsSorted.length > 0 ? monthsSorted : monthOrder.slice(0, 6);
    },
    getOrCreateChartContainer(): HTMLElement | null {
      // Use existing container if present
      let container = (this.$el as HTMLElement).querySelector(".chart-show-img") as HTMLElement | null;
      if (container) return container;

      // Create a minimal container structure inside the root chart-box
      const root = this.$el as HTMLElement; // .chart-box
      const chartBtm = document.createElement("div");
      chartBtm.className = "chart-btm";
      container = document.createElement("div");
      container.className = "chart-show-img";
      chartBtm.appendChild(container);
      root.appendChild(chartBtm);
      return container;
    },
    showSpinner() {
      const container = this.getOrCreateChartContainer();
      if (!container) return;
      // If spinner already exists, do nothing
      if (container.querySelector('[data-role="chart-loading"]')) return;

      const spinnerWrap = document.createElement("div");
      spinnerWrap.className = "chart3-loading-container";
      spinnerWrap.setAttribute("data-role", "chart-loading");

      const spinner = document.createElement("div");
      spinner.className = "chart3-loading-spinner";
      spinner.textContent = "Loading sprint data...";

      spinnerWrap.appendChild(spinner);
      container.appendChild(spinnerWrap);

      // Hide existing canvas while loading so spinner is visible
      const existingCanvas = container.querySelector('canvas') as HTMLCanvasElement | null;
      if (existingCanvas) existingCanvas.style.display = 'none';
    },
    hideSpinner() {
      const container = this.getOrCreateChartContainer();
      if (!container) return;
      const spinner = container.querySelector('[data-role="chart-loading"]');
      if (spinner) spinner.remove();

      // Show canvas back after loading
      const existingCanvas = container.querySelector('canvas') as HTMLCanvasElement | null;
      if (existingCanvas) existingCanvas.style.display = 'block';
    },
    injectCanvasAndRender() {
      const container = this.getOrCreateChartContainer();
      if (!container) return;

      // Hide any legacy static image if present
      const img = container.querySelector("img") as HTMLImageElement | null;
      if (img) img.style.display = "none";

      // Ensure a canvas exists
      let canvas = container.querySelector("canvas") as HTMLCanvasElement | null;
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvas.width = 400;
        canvas.height = 200;
        canvas.className = 'chart3-canvas';
        container.appendChild(canvas);
      }
      // Ensure canvas has desired size/class if it already existed
      canvas.width = 400;
      canvas.height = 200;
      canvas.classList.add('chart3-canvas');

      this.renderChart();
    },
    getColorForProject(projectKey: string): string {
      // Stable color per project using hash -> hue
      let hash = 0;
      for (let i = 0; i < projectKey.length; i++) {
        hash = projectKey.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash) % 360;
      return `hsl(${hue}, 70%, 45%)`;
    },
    buildDatasets(metric: "created" | "resolved") {
      const datasets: any[] = [];
      Object.values(this.projectsData).forEach((proj) => {
        const series = this.months.map((m) => {
          const found = proj.monthly_data.find((pt) => pt.month === m);
          return found ? (metric === "created" ? found.created : found.resolved) : 0;
        });
        const color = this.getColorForProject(proj.project_key);
        datasets.push({
          label: `${proj.project_name} (${proj.project_key})`,
          data: series,
          borderColor: color,
          backgroundColor: color,
          tension: 0.25,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: color,
          pointBorderWidth: 2,
        });
      });
      return datasets;
    },
    computeYScaleMax(datasets: any[]) {
      let maxVal = 0;
      datasets.forEach((ds) => {
        ds.data.forEach((v: number) => {
          if (v > maxVal) maxVal = v;
        });
      });
      const rounded = Math.ceil(Math.max(maxVal, 100) / 100) * 100;
      // Ensure at least 0-1000 range, expand if data exceeds 1000
      return Math.max(1000, rounded);
    },
    computeYAxis(datasets: any[]) {
      let maxVal = 0;
      datasets.forEach((ds) => {
        ds.data.forEach((v: number) => { if (v > maxVal) maxVal = v; });
      });

      if (!isFinite(maxVal) || maxVal <= 0) {
        return { min: 0, max: 10, stepSize: 2 };
      }

      const desiredTicks = 5; // target 5 grid lines for a 200px tall chart
      const rawStep = maxVal / desiredTicks;
      const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
      const residual = rawStep / magnitude;
      let niceResidual = 1;
      if (residual <= 1) niceResidual = 1;
      else if (residual <= 2) niceResidual = 2;
      else if (residual <= 5) niceResidual = 5;
      else niceResidual = 10;
      let step = niceResidual * magnitude;
      if (step < 1) step = 1;

      // Add one extra step of headroom above highest value
      const yMax = Math.ceil(maxVal / step) * step + step;
      const yMin = 0;

      return { min: yMin, max: yMax, stepSize: step };
    },
    renderChart() {
      if (this.isRendering) {
        // Avoid concurrent renders; queue another pass
        this.queueRender();
        return;
      }
      this.isRendering = true;
      const container = this.getOrCreateChartContainer();
      if (!container) return;
      const canvas = container.querySelector("canvas") as HTMLCanvasElement | null;
      if (!canvas) return;
      // Ensure canvas is visible before getting context
      canvas.style.display = 'block';

      if (this.chart) {
        try { if (typeof this.chart.stop === 'function') this.chart.stop(); } catch (_) {}
        try { this.chart.destroy(); } catch (_) {}
        this.chart = null;
      }

      const datasets = this.buildDatasets(this.selectedMetric);
      const yAxis = this.computeYAxis(datasets);

      const ctx = canvas.getContext("2d");
      if (!ctx) { this.isRendering = false; return; }

      try {
        this.chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: this.months,
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: true,
            animation: { duration: 0 },
            plugins: {
              legend: { display: true, position: "bottom" },
              title: {
                display: true,
                text: this.selectedMetric === "created"
                  ? "Created Issues per Month by Project"
                  : "Resolved Issues per Month by Project",
                font: { size: 16 },
              },
              tooltip: {
                mode: "nearest",
                intersect: false,
                callbacks: {
                  label: function (context: any) {
                    const label = context.dataset.label || "";
                    const val = context.parsed.y ?? 0;
                    return `${label}: ${val} tickets`;
                  },
                },
              },
            },
            interaction: { mode: "index", intersect: false },
            scales: {
              x: { title: { display: true, text: "Months" } },
              y: {
                beginAtZero: true,
                min: yAxis.min,
                max: yAxis.max,
                ticks: { stepSize: yAxis.stepSize, maxTicksLimit: 7 },
                title: { display: true, text: "Tickets" },
              },
            },
            elements: { point: { pointStyle: "circle" } },
          },
        });
      } finally {
        this.isRendering = false;
      }
    },
  },
});
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #007bff;
  font-size: 16px;
}

.loading-spinner::before {
  content: '';
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<style>
.chart3-loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}

.chart3-loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #007bff;
  font-size: 16px;
}

.chart3-loading-spinner::before {
  content: '';
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: chart3-spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes chart3-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<style scoped>
.chart3-canvas {
  max-width: 650px;
  width: 100% !important;
  height: auto !important;
  display: block;
  margin: 0 auto;
}
</style>
